import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { errMsg, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import {
  EMPTY_PROFILE, EducationSection, SituationSection, SkillsSection, StudySection, TargetSection, WorkSection, cleanProfile,
} from "@/components/learner/ProfileSections";

const STEPS = [
  { title: "What best describes you right now?", valid: (p) => !!p.situation },
  { title: "Your education", sub: "Add what you have. You can skip this step." },
  { title: "Your work history", sub: "Include internships, volunteering or part-time work." },
  { title: "Where do you want to go?", valid: (p) => p.target_role.trim().length >= 2 },
  { title: "Skills you already have", sub: "These start as self-declared. Assessments and projects will verify them later." },
  { title: "How do you like to learn?" },
];

export default function LearnerOnboarding() {
  const api = useApi();
  const navigate = useNavigate();
  const { refresh } = useMe();
  const [step, setStep] = useState(0);
  const [p, setP] = useState(EMPTY_PROFILE);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const cur = STEPS[step];
  const canNext = !cur.valid || cur.valid(p);

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/me/learner-profile", cleanProfile(p));
      await refresh();
      toast.success("Profile saved. Welcome to Crowlearn!");
      navigate("/learn", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const body = [
    <SituationSection value={p.situation} onChange={(v) => set("situation", v)} />,
    <EducationSection items={p.education} onChange={(v) => set("education", v)} />,
    <WorkSection items={p.work_history} onChange={(v) => set("work_history", v)} />,
    <TargetSection p={p} set={set} />,
    <SkillsSection skills={p.skills} onChange={(v) => set("skills", v)} />,
    <StudySection p={p} set={set} />,
  ][step];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b border-slate-200 bg-white px-4 py-3 sm:px-8"><Logo /></header>
      <div className="mx-auto max-w-2xl p-4 sm:p-8">
        <div className="mb-6 flex items-center justify-between text-xs text-slate-500">
          <span className="font-mono-label uppercase tracking-[0.2em]" data-testid="onboarding-step-label">Step {step + 1} of {STEPS.length}</span>
          <span>Learner setup</span>
        </div>
        <Progress value={((step + 1) / STEPS.length) * 100} className="mb-8 h-1.5" />
        <div key={step} className="rise rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="onboarding-step-title">{cur.title}</h1>
          {cur.sub && <p className="mt-2 text-sm text-slate-600">{cur.sub}</p>}
          <div className="mt-6">{body}</div>
        </div>
        <div className="mt-6 flex justify-between">
          <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="onboarding-back-btn"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext} className="rounded-full bg-[#222d99] px-6 hover:bg-[#1a237e]" data-testid="onboarding-next-btn">
              Next <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={finish} disabled={busy} className="rounded-full bg-[#222d99] px-6 hover:bg-[#1a237e]" data-testid="onboarding-finish-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Finish setup
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
